import {
  InvApplication,
  ApplicationHelpers,
  type SecurityAttributeSetting
} from '@invision/core';

/**
 * Module Access Validation using existing ApplicationHelpers
 *
 * This leverages the centralized application configuration from @invision/core
 * instead of duplicating module definitions and security attributes.
 */

export interface ModuleAccessRequirement {
  /** Application configuration from core */
  application: InvApplication;
  /** Alternative paths that map to this application */
  alternativePaths?: string[];
}

/**
 * Get the application configuration for a given path
 * Uses the centralized application configuration from core
 */
export function getModuleAccessRequirement(
  path: string
): ModuleAccessRequirement | null {
  // Find the application that matches this path
  const matchingApp = Object.values(ApplicationHelpers.allApplications).find(
    (app) =>
      path.startsWith(app.basePath) ||
      (!!app.angularBasePath && path.startsWith(app.angularBasePath))
  );

  if (matchingApp) {
    return {
      application: matchingApp,
      alternativePaths: undefined // Core applications already define their canonical paths
    };
  }

  return null;
}

/**
 * Get all applications the user has access to, ordered by preference
 * Uses the existing core logic for determining access
 */
export function getAccessibleApplications(
  userSecurityAttributes: SecurityAttributeSetting[]
): InvApplication[] {
  return ApplicationHelpers.determineApplicationAccessForSecurityAttributes(
    userSecurityAttributes
  ).sort(
    (a: InvApplication, b: InvApplication) => (a.order || 99) - (b.order || 99)
  );
}

/**
 * Get fallback application paths in order of preference
 * Based on the centralized application order configuration
 */
export function getFallbackModulePriorities(
  userSecurityAttributes: SecurityAttributeSetting[]
): string[] {
  const accessibleApps = getAccessibleApplications(userSecurityAttributes);

  return accessibleApps.map(ApplicationHelpers.getApplicationEntryPath);
}
